import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";

import { loadGoogleIdentity, GOOGLE_CLIENT_ID, isGoogleSignInEnabled } from "/imports/libs/googleIdentity";

// Google's branding terms fix the wording; only these three are allowed.
const LABELS = {
  continue_with: "Continue with Google",
  signin_with: "Sign in with Google",
  signup_with: "Sign up with Google"
};

const SCOPE = "openid email profile";

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  );
}

/**
 * "Continue with Google" via the authorization-code flow: Google returns a
 * one-time code to the popup opener and the API exchanges it for the identity,
 * so nothing here is trusted and the button is ours to style.
 */
function GoogleSignInButton({ onCode, text = "continue_with", disabled = false }) {
  const codeClient = useRef(null);
  const handler = useRef(onCode);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    handler.current = onCode;
  }, [onCode]);

  useEffect(() => {
    if (!isGoogleSignInEnabled()) {
      setUnavailable(true);
      return undefined;
    }

    let cancelled = false;

    loadGoogleIdentity()
      .then((accounts) => {
        if (cancelled) return;

        codeClient.current = accounts.oauth2.initCodeClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPE,
          ux_mode: "popup",
          callback: (response) => {
            setPending(false);
            if (response?.code) handler.current?.(response.code);
          },
          // Fires when the popup is dismissed or blocked — not an error worth
          // showing, the client simply changed their mind.
          error_callback: () => setPending(false)
        });

        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = useCallback(() => {
    if (!codeClient.current) return;
    setPending(true);
    codeClient.current.requestCode();
  }, []);

  if (unavailable) return null;
  if (!ready) return <Skeleton variant="rounded" height="2.625rem" />;

  return (
    <Button
      fullWidth
      variant="outlined"
      color="inherit"
      onClick={handleClick}
      disabled={disabled || pending}
      startIcon={pending ? <CircularProgress size="1.125rem" color="inherit" /> : <GoogleLogo />}
      sx={{
        height: "2.625rem",
        textTransform: "none",
        fontSize: "0.9375rem",
        color: "text.primary",
        borderColor: "divider",
        backgroundColor: "background.paper",
        "&:hover": {
          borderColor: "primary.main",
          backgroundColor: "action.hover"
        }
      }}
    >
      {LABELS[text] || LABELS.continue_with}
    </Button>
  );
}

export default GoogleSignInButton;
